/**
 * The LexPortal **Specialists** section, per
 * `ai-docs/lexportal-specialists-surface-miniplan.md` and the
 * `lexiun-agent-specialists` handoff of 2026-08-20.
 *
 * Three of the seven planned surfaces are live: S1 roster (+ S2 attributes at
 * view-only), S4 workspace navigator at VIEW, and S7 home health. The rest wait
 * on lexserver slices — S3 on the composition gate, S4's edit half on kb write,
 * S5 on the quarantine verbs — and are absent rather than stubbed, because an
 * empty tab on a monitoring console reads as "nothing here" for a surface that
 * simply has no backend yet.
 *
 * Tabs rather than one long page: the three answer different questions (who
 * exists · what is in their workspace · is the substrate healthy) and each owns
 * its own reads, so a failure in one never blanks another.
 */

import { FolderTree, HeartPulse, Users } from "lucide-react";
import { useState } from "react";

import { SpecialistHealth } from "@/components/specialists/SpecialistHealth";
import { SpecialistRoster } from "@/components/specialists/SpecialistRoster";
import { SpecialistWorkspace } from "@/components/specialists/SpecialistWorkspace";

const TABS = [
  { id: "roster", label: "Roster", icon: Users, Component: SpecialistRoster },
  { id: "workspace", label: "Workspace", icon: FolderTree, Component: SpecialistWorkspace },
  { id: "health", label: "Health", icon: HeartPulse, Component: SpecialistHealth },
] as const;

type TabId = (typeof TABS)[number]["id"];

export function Specialists() {
  const [active, setActive] = useState<TabId>("roster");
  const Active = TABS.find((t) => t.id === active)?.Component ?? SpecialistRoster;

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="specialists-page">
      <h1 className="mb-3 shrink-0 text-xl font-semibold">Specialists</h1>

      <div
        className="mb-3 flex shrink-0 flex-wrap items-center gap-0.5 border-b border-slate-200 dark:border-slate-800"
        role="tablist"
        aria-label="Specialists sections"
      >
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const on = active === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setActive(tab.id)}
              data-testid={`specialists-tab-${tab.id}`}
              className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3 py-2 text-xs font-medium transition-colors ${
                on
                  ? "border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400"
                  : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <Active />
      </div>
    </div>
  );
}
